import type {
  CandidateConsentExpiryFilter,
  CandidateQueryParams,
  CandidateStatus,
  CandidateVerifiedFilter,
  GdprStatus,
} from '@/types/candidates';

// The candidate list's URL search: which values are kept, and which are left
// out of the URL. Moved out of routes/recruitment.candidates.tsx so it can be
// tested in the node test environment [HW-1023].

export type CandidateSortField = NonNullable<CandidateQueryParams['sortBy']>;
export type CandidateSortOrder = NonNullable<CandidateQueryParams['sortOrder']>;
export type CandidateActiveVerified = Exclude<CandidateVerifiedFilter, 'any'>;

// HW-590 (extended to Candidates): persist the list filters the same way as the
// Vacancies list — the URL query is the source of truth (shareable/bookmarkable)
// and is mirrored to localStorage per user so the selection also survives a fresh
// navigation after re-login. Defaults (ALL / any / updatedAt desc / empty search)
// are kept OUT of the URL so a pristine view stays on a clean URL.
export type CandidatesSearch = {
  status?: CandidateStatus;
  city?: string;
  tag?: string;
  verified?: CandidateActiveVerified;
  gdprStatus?: GdprStatus;
  // HW-1023: "Consent ending soon / expired" — offered in the GDPR select,
  // so it and `gdprStatus` are never set together.
  consentExpiry?: CandidateConsentExpiryFilter;
  q?: string;
  sortBy?: CandidateSortField;
  sortOrder?: CandidateSortOrder;
};

// The GDPR select's value for the consent-ending option; never a GdprStatus.
export const CONSENT_ENDING_OPTION = 'CONSENT_ENDING';

const VALID_CAND_STATUSES = new Set<string>(['ACTIVE', 'HIRING', 'HIRED', 'ARCHIVED']);
const VALID_GDPR_STATUSES = new Set<string>(['PENDING', 'APPROVED', 'EXPIRED', 'REVOKED']);
const VALID_VERIFIED = new Set<string>(['verified', 'unverified']);
const VALID_CAND_SORT_FIELDS = new Set<string>(['fullName', 'status', 'createdAt', 'updatedAt']);
const VALID_CAND_SORT_ORDERS = new Set<string>(['asc', 'desc']);

export const DEFAULT_CAND_SORT_BY: CandidateSortField = 'updatedAt';
export const DEFAULT_CAND_SORT_ORDER: CandidateSortOrder = 'desc';

// Accepts an untrusted bag (URL query or a localStorage payload) and keeps only
// values that map to a real filter. Shared by `validateSearch` and the
// localStorage hydration so a stale/garbage entry can't resurrect an invalid
// filter. `city`/`tag` are dynamic free-text options, so any non-empty string is
// accepted (a now-stale value simply matches nothing).
export function sanitizeCandidatesSearch(raw: Record<string, unknown>): CandidatesSearch {
  const out: CandidatesSearch = {};
  if (typeof raw.status === 'string' && VALID_CAND_STATUSES.has(raw.status)) {
    out.status = raw.status as CandidateStatus;
  }
  if (typeof raw.city === 'string' && raw.city.length > 0) out.city = raw.city;
  if (typeof raw.tag === 'string' && raw.tag.length > 0) out.tag = raw.tag;
  if (typeof raw.verified === 'string' && VALID_VERIFIED.has(raw.verified)) {
    out.verified = raw.verified as CandidateActiveVerified;
  }
  if (typeof raw.gdprStatus === 'string' && VALID_GDPR_STATUSES.has(raw.gdprStatus)) {
    out.gdprStatus = raw.gdprStatus as GdprStatus;
  }
  // HW-1023: the consent-ending option and a GDPR status share one select, so
  // a URL or stored payload carrying both keeps the option the select shows.
  if (raw.consentExpiry === 'ending') {
    out.consentExpiry = 'ending';
    delete out.gdprStatus;
  }
  if (typeof raw.q === 'string' && raw.q.length > 0) out.q = raw.q;
  if (typeof raw.sortBy === 'string' && VALID_CAND_SORT_FIELDS.has(raw.sortBy)) {
    out.sortBy = raw.sortBy as CandidateSortField;
  }
  if (typeof raw.sortOrder === 'string' && VALID_CAND_SORT_ORDERS.has(raw.sortOrder)) {
    out.sortOrder = raw.sortOrder as CandidateSortOrder;
  }
  return out;
}

// Drops defaults so the URL (and stored payload) only carry an active selection.
export function normalizeCandidatesSearch(s: CandidatesSearch): CandidatesSearch {
  const out: CandidatesSearch = {};
  if (s.status) out.status = s.status;
  if (s.city) out.city = s.city;
  if (s.tag) out.tag = s.tag;
  if (s.verified) out.verified = s.verified;
  // HW-1023: never both — see sanitizeCandidatesSearch.
  if (s.consentExpiry) out.consentExpiry = s.consentExpiry;
  else if (s.gdprStatus) out.gdprStatus = s.gdprStatus;
  if (s.q) out.q = s.q;
  if (s.sortBy && s.sortBy !== DEFAULT_CAND_SORT_BY) out.sortBy = s.sortBy;
  if (s.sortOrder && s.sortOrder !== DEFAULT_CAND_SORT_ORDER) out.sortOrder = s.sortOrder;
  return out;
}
